/**
 * Property card "Vraag info aan" → scroll to on-page contact (#main-contact) and
 * prefill property reference (title) into ClickDimensions and/or native contact form.
 *
 * Plan download opens a modal with an iframe CD form — prefill only after the modal
 * (and iframe survey) are mounted, not on the on-page contact section.
 */
import {
	getAllClickDimensionsSurveyModels,
	getClickDimensionsSearchScopes,
} from './clickdimensions-hidden-fields.js';

const STORAGE_KEY = 'heizijde_property_inquiry_ref';
const STORAGE_PROJECT = 'heizijde_property_inquiry_project';
const STORAGE_PROPERTY = 'heizijde_property_inquiry_property';
/** Same key as property-plan-download.js */
const STORAGE_PLAN_ID = 'heizijde_property_plan_id';

/** @type {ReturnType<typeof window.setInterval> | null} */
let cdRetryTimer = null;

function getConfig() {
	return window.vtcPropertyInquiry || window.heizijdePropertyInquiry || {};
}

function getContactSection() {
	const id = (getConfig().contactSectionId || 'main-contact').replace(/^#/, '');
	return document.getElementById(id);
}

/** @returns {HTMLElement|null} */
function getOpenModalOverlay() {
	const overlay = document.querySelector(
		'.modal-overlay.is-open, .modal-overlay.is-active, .modal-overlay[aria-hidden="false"]:not(.heizijde-modal-preload-host)'
	);
	return overlay instanceof HTMLElement ? overlay : null;
}

function isPlanDownloadFlow() {
	try {
		return Boolean(sessionStorage.getItem(STORAGE_PLAN_ID));
	} catch {
		return false;
	}
}

/**
 * Scope for CD prefill: open modal when present; on-page contact for inquiry links only.
 *
 * @returns {ParentNode|null} null when plan-download modal is not open yet.
 */
function getPropertyFormScope() {
	const modal = getOpenModalOverlay();
	if (modal) {
		const mainColumn = modal.querySelector('.modal-column--main');
		if (mainColumn) {
			return mainColumn;
		}

		const formRoot = modal.querySelector(
			'.modal-body__form, .modal-event__form, .heizijde-cd-form'
		);
		if (formRoot) {
			return (
				formRoot.closest('.modal-column--main') ||
				formRoot.closest('.modal-event__main') ||
				formRoot.closest('.modal-body') ||
				formRoot
			);
		}
		const main = modal.querySelector('.modal-event__main, .modal-body');
		if (main) {
			return main;
		}
		return modal;
	}

	if (isPlanDownloadFlow()) {
		return null;
	}

	return getContactSection() || document;
}

function getConfigFieldNameList(key, primaryKey = '') {
	const config = getConfig();
	const names = [];
	if (primaryKey) {
		const primary = String(config[primaryKey] || '').trim();
		if (primary) {
			names.push(primary);
		}
	}
	const extra = config[key];
	if (Array.isArray(extra)) {
		for (const name of extra) {
			const trimmed = String(name || '').trim();
			if (trimmed && !names.includes(trimmed)) {
				names.push(trimmed);
			}
		}
	}
	return names;
}

function getCdFieldNames() {
	return getConfigFieldNameList('cdFieldNames', 'cdFieldName');
}

function getCdProjectFieldNames() {
	return getConfigFieldNameList('cdProjectFieldNames');
}

function getCdPropertyFieldNames() {
	return getConfigFieldNameList('cdPropertyFieldNames');
}

function readStored(key) {
	try {
		return sessionStorage.getItem(key) || '';
	} catch {
		return '';
	}
}

function writeStored(key, value) {
	try {
		if (value) {
			sessionStorage.setItem(key, value);
		} else {
			sessionStorage.removeItem(key);
		}
	} catch {
		/* private mode etc. */
	}
}

function getPendingInquiry() {
	return {
		ref: readStored(STORAGE_KEY),
		project: readStored(STORAGE_PROJECT),
		property: readStored(STORAGE_PROPERTY),
	};
}

function hasPendingInquiry() {
	const pending = getPendingInquiry();
	return Boolean(pending.ref || pending.project || pending.property);
}

function clearPendingInquiry() {
	writeStored(STORAGE_KEY, '');
	writeStored(STORAGE_PROJECT, '');
	writeStored(STORAGE_PROPERTY, '');
}

function stopCdRetry() {
	if (cdRetryTimer !== null) {
		window.clearInterval(cdRetryTimer);
		cdRetryTimer = null;
	}
}

function setNativePropertyField(ref) {
	const el = document.querySelector('#contact-property-reference');
	if (el && 'value' in el) {
		el.value = ref;
	}
}

function domSelectorsForFieldName(fieldName) {
	const raw = String(fieldName || '').trim();
	if (!raw) {
		return [];
	}
	const slug = raw.toLowerCase();
	const names = slug === raw ? [slug] : [slug, raw];
	const selectors = [];
	for (const name of names) {
		selectors.push(
			`[data-name="${name}"] input`,
			`[data-name="${name}"] textarea`,
			`[data-name="${name}"] [contenteditable="true"]`,
			`[data-question-name="${name}"] input`,
			`[data-question-name="${name}"] textarea`,
			`input[name="${name}"]`,
			`textarea[name="${name}"]`
		);
	}
	return selectors;
}

function trySetClickDimensionsFieldDom(value, fieldNames, section) {
	if (!section || !value || !fieldNames.length) {
		return false;
	}
	const selectors = fieldNames.flatMap(domSelectorsForFieldName);
	for (const searchScope of getClickDimensionsSearchScopes(section)) {
		for (const selector of selectors) {
			const el = searchScope.querySelector(selector);
			if (el && 'value' in el) {
				el.value = value;
				el.dispatchEvent(new Event('input', { bubbles: true }));
				el.dispatchEvent(new Event('change', { bubbles: true }));
				return true;
			}
		}
	}
	return false;
}

function normalizeFieldKey(value) {
	return String(value || '')
		.trim()
		.toLowerCase()
		.replace(/\s+/g, '');
}

function trySetClickDimensionsFieldByQuestion(model, value, fieldNames, looseTitle = true) {
	if (!model || typeof model.getAllQuestions !== 'function') {
		return false;
	}

	const needles = fieldNames.map((n) => String(n).trim().toLowerCase()).filter(Boolean);
	const needleKeys = needles.map(normalizeFieldKey).filter(Boolean);
	if (!needles.length) {
		return false;
	}

	let applied = false;

	for (const question of model.getAllQuestions()) {
		const name = question?.name ? String(question.name) : '';
		const title = question?.title ? String(question.title).toLowerCase() : '';
		const nameKey = normalizeFieldKey(name);
		const titleKey = normalizeFieldKey(title);
		const matches = needleKeys.some((needle) => needle === nameKey || needle === titleKey) ||
			(looseTitle && needles.some((needle) => title.includes(needle)));

		if (!matches || !name) {
			continue;
		}

		try {
			model.setValue(name, value);
			applied = true;
		} catch {
			/* try next question */
		}
	}

	return applied;
}

function trySetClickDimensionsFields(value, fieldNames, looseTitle = true) {
	if (!fieldNames.length || !value) {
		return false;
	}

	const section = getPropertyFormScope();
	if (!section) {
		return false;
	}

	let applied = false;
	let sawSurveyModel = false;

	for (const model of getAllClickDimensionsSurveyModels(section)) {
		sawSurveyModel = true;
		if (trySetClickDimensionsFieldByQuestion(model, value, fieldNames, looseTitle)) {
			applied = true;
			continue;
		}

		if (typeof model.getAllQuestions !== 'function') {
			for (const fieldName of fieldNames) {
				try {
					model.setValue(fieldName, value);
					applied = true;
				} catch {
					/* try next key */
				}
			}
		}
	}

	if (applied) {
		return true;
	}

	if (sawSurveyModel) {
		return false;
	}

	return trySetClickDimensionsFieldDom(value, fieldNames, section);
}

function tryApplyPendingInquiryFields() {
	const pending = getPendingInquiry();
	if (!pending.ref && !pending.project && !pending.property) {
		return true;
	}

	const section = getPropertyFormScope();
	if (!section) {
		return false;
	}

	const descNames = getCdFieldNames();
	const projectNames = getCdProjectFieldNames();
	const propertyNames = getCdPropertyFieldNames();

	if (!descNames.length && !projectNames.length && !propertyNames.length) {
		return true;
	}

	let descOk = true;
	const descriptionValue = pending.property || pending.ref;
	if (descriptionValue && descNames.length) {
		descOk = trySetClickDimensionsFields(descriptionValue, descNames);
	}

	if (pending.project && projectNames.length) {
		trySetClickDimensionsFields(pending.project, projectNames, false);
	}
	if (pending.property && propertyNames.length) {
		trySetClickDimensionsFields(pending.property, propertyNames, false);
	}

	return descOk;
}

function scheduleCdRetry() {
	if (cdRetryTimer !== null) {
		return;
	}

	let attempts = 0;
	const maxAttempts = isPlanDownloadFlow() ? 150 : 60;

	cdRetryTimer = window.setInterval(() => {
		attempts += 1;
		if (tryApplyPendingInquiryFields()) {
			stopCdRetry();
			clearPendingInquiry();
		} else if (attempts >= maxAttempts) {
			stopCdRetry();
			if (!isPlanDownloadFlow() && !getOpenModalOverlay()) {
				clearPendingInquiry();
			}
		}
	}, 100);
}

/**
 * Apply stored project / property / combined reference to DOM + CD form.
 */
export function applyPendingPropertyReference() {
	const pending = getPendingInquiry();
	if (!pending.ref && !pending.project && !pending.property) {
		return;
	}

	const planDownload = isPlanDownloadFlow();

	if (!planDownload && pending.ref) {
		setNativePropertyField(pending.ref);
	}

	const descNames = getCdFieldNames();
	const projectNames = getCdProjectFieldNames();
	const propertyNames = getCdPropertyFieldNames();

	if (!descNames.length && !projectNames.length && !propertyNames.length) {
		if (!planDownload || getPropertyFormScope()) {
			clearPendingInquiry();
		}
		return;
	}

	if (!getPropertyFormScope()) {
		scheduleCdRetry();
		return;
	}

	if (tryApplyPendingInquiryFields()) {
		clearPendingInquiry();
		return;
	}

	scheduleCdRetry();
}

function schedulePlanDownloadPrefillAttempts() {
	if (!isPlanDownloadFlow() || !hasPendingInquiry()) {
		return;
	}

	applyPendingPropertyReference();
	for (const delay of [150, 400, 800, 1500]) {
		window.setTimeout(() => applyPendingPropertyReference(), delay);
	}
}

function readReferenceFromLink(anchor) {
	const fromData = anchor.dataset?.propertyReference;
	if (fromData) {
		return fromData.trim();
	}
	const card = anchor.closest('.vzp-facetwp-card, .block-property-offering-card');
	const titleEl = card?.querySelector('.property-card__title');
	if (titleEl) {
		return titleEl.textContent.replace(/\s+/g, ' ').trim();
	}

	const row = anchor.closest('.vzp-facetwp-properties-table__row');
	const tableTitle = row?.querySelector('.vzp-facetwp-properties-table__project');
	if (tableTitle) {
		return tableTitle.textContent.replace(/\s+/g, ' ').trim();
	}

	return '';
}

function splitCombinedReference(ref) {
	const marker = ' - ';
	const idx = ref.indexOf(marker);
	if (idx === -1) {
		return { project: '', property: ref };
	}
	return {
		project: ref.slice(0, idx).trim(),
		property: ref.slice(idx + marker.length).trim(),
	};
}

function readInquiryFromLink(anchor) {
	const ref = readReferenceFromLink(anchor);
	const parsed = splitCombinedReference(ref);
	const project = (anchor.dataset?.projectName || '').trim() || parsed.project;
	const property = (anchor.dataset?.propertyName || '').trim() || parsed.property;

	return { ref, project, property };
}

function storeInquiry(inquiry) {
	writeStored(STORAGE_KEY, inquiry.ref);
	writeStored(STORAGE_PROJECT, inquiry.project);
	writeStored(STORAGE_PROPERTY, inquiry.property);
}

function handlePlanDownloadClick(planAnchor) {
	storeInquiry(readInquiryFromLink(planAnchor));
	// Form loads in an iframe after modal:opened — prefill via modal / iframe events.
}

function onDocumentClick(e) {
	const planAnchor = e.target.closest('a.js-property-plan-download');
	if (planAnchor) {
		handlePlanDownloadClick(planAnchor);
		return;
	}

	const anchor = e.target.closest('a.js-property-inquiry-link');
	if (!anchor) {
		return;
	}
	const href = anchor.getAttribute('href') || '';
	if (!href.startsWith('#')) {
		return;
	}
	storeInquiry(readInquiryFromLink(anchor));

	// Same-hash navigation does not fire hashchange; still prefill after scroll.
	window.requestAnimationFrame(() => {
		applyPendingPropertyReference();
	});
}

function onDomReady(fn) {
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', fn, { once: true });
	} else {
		fn();
	}
}

function init() {
	// Capture phase: store property reference before modal-handler opens the overlay.
	document.addEventListener('click', onDocumentClick, true);

	onDomReady(() => {
		applyPendingPropertyReference();
	});

	window.addEventListener('hashchange', () => {
		applyPendingPropertyReference();
	});

	document.addEventListener('facetwp-loaded', () => {
		applyPendingPropertyReference();
	});

	document.addEventListener('heizijde:clickdimensions-mount', (e) => {
		if (!hasPendingInquiry()) {
			return;
		}

		const container = e.detail?.container;
		if (!container) {
			applyPendingPropertyReference();
			return;
		}

		const openModal = getOpenModalOverlay();
		const inOpenModal = Boolean(openModal?.contains(container));
		const inContact = Boolean(getContactSection()?.contains(container));
		const inPreload = Boolean(
			container.closest?.('.heizijde-modal-preload-host, #heizijde-modal-preload')
		);

		if (inPreload && !inOpenModal) {
			return;
		}
		if (openModal && !inOpenModal && !inContact) {
			return;
		}

		if (isPlanDownloadFlow() && inOpenModal) {
			schedulePlanDownloadPrefillAttempts();
			return;
		}

		applyPendingPropertyReference();
	});

	document.addEventListener('modal:opened', () => {
		if (!hasPendingInquiry()) {
			return;
		}
		if (isPlanDownloadFlow()) {
			schedulePlanDownloadPrefillAttempts();
			return;
		}
		applyPendingPropertyReference();
	});

	document.addEventListener('heizijde:clickdimensions-iframe-ready', (e) => {
		if (!hasPendingInquiry()) {
			return;
		}
		const iframe = e.detail?.iframe;
		const openModal = getOpenModalOverlay();
		if (iframe instanceof HTMLIFrameElement && openModal?.contains(iframe)) {
			schedulePlanDownloadPrefillAttempts();
		}
	});

	document.addEventListener('modal:closed', () => {
		if (!isPlanDownloadFlow()) {
			return;
		}
		window.setTimeout(() => {
			if (!isPlanDownloadFlow()) {
				stopCdRetry();
				clearPendingInquiry();
			}
		}, 4500);
	});
}

init();
